import * as Tone from 'tone';

/**
 * Procedural SFX engine using Tone.js synthesis.
 * Each sound is generated on-the-fly — no audio files needed.
 */
export class SFXEngine {
  private synth: Tone.Synth | null = null;
  private noiseSynth: Tone.NoiseSynth | null = null;
  private volume: number = 1.0;
  private initialized: boolean = false;
  private lastPlayTime: number = 0;
  private throttleMs: number = 50;

  async init(): Promise<void> {
    if (this.initialized) return;
    await Tone.start();

    this.synth = new Tone.Synth({
      oscillator: { type: 'square' },
      envelope: { attack: 0.005, decay: 0.1, sustain: 0, release: 0.1 },
    }).toDestination();

    this.noiseSynth = new Tone.NoiseSynth({
      noise: { type: 'white' },
      envelope: { attack: 0.001, decay: 0.1, sustain: 0, release: 0.05 },
    }).toDestination();

    this.initialized = true;
  }

  setVolume(vol: number): void {
    this.volume = Math.max(0, Math.min(1, vol));
    if (this.synth) {
      this.synth.volume.value = vol > 0 ? Tone.gainToDb(vol) : -Infinity;
    }
  }

  private canPlay(): boolean {
    const now = Date.now();
    if (now - this.lastPlayTime < this.throttleMs) return false;
    this.lastPlayTime = now;
    return this.initialized && this.volume > 0;
  }

  // ─── Individual SFX ────────────────────────────────────────

  click(): void {
    if (!this.canPlay() || !this.synth) return;
    this.synth.triggerAttackRelease('C5', '32n', undefined, 0.3);
  }

  success(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('C5', '16n', now, 0.4);
    this.synth.triggerAttackRelease('E5', '16n', now + 0.08, 0.4);
    this.synth.triggerAttackRelease('G5', '16n', now + 0.16, 0.5);
  }

  levelUp(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('C5', '16n', now, 0.5);
    this.synth.triggerAttackRelease('E5', '16n', now + 0.07, 0.5);
    this.synth.triggerAttackRelease('G5', '16n', now + 0.14, 0.6);
    this.synth.triggerAttackRelease('C6', '8n', now + 0.21, 0.7);
  }

  warning(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('E4', '16n', now, 0.5);
    this.synth.triggerAttackRelease('C4', '16n', now + 0.12, 0.5);
  }

  error(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('C3', '8n', now, 0.4);
    this.synth.triggerAttackRelease('B2', '8n', now + 0.15, 0.3);
  }

  coin(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('E6', '32n', now, 0.3);
    this.synth.triggerAttackRelease('B6', '16n', now + 0.05, 0.4);
  }

  reviewHigh(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('G5', '16n', now, 0.5);
    this.synth.triggerAttackRelease('B5', '16n', now + 0.1, 0.5);
    this.synth.triggerAttackRelease('D6', '8n', now + 0.2, 0.6);
  }

  reviewLow(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('D4', '8n', now, 0.4);
    this.synth.triggerAttackRelease('Bb3', '4n', now + 0.15, 0.3);
  }

  gameOver(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('E4', '8n', now, 0.5);
    this.synth.triggerAttackRelease('D4', '8n', now + 0.2, 0.4);
    this.synth.triggerAttackRelease('C4', '8n', now + 0.4, 0.4);
    this.synth.triggerAttackRelease('B3', '2n', now + 0.6, 0.3);
  }

  unlock(): void {
    if (!this.canPlay() || !this.synth) return;
    const now = Tone.now();
    this.synth.triggerAttackRelease('G5', '16n', now, 0.4);
    this.synth.triggerAttackRelease('A5', '16n', now + 0.06, 0.5);
    this.synth.triggerAttackRelease('C6', '8n', now + 0.12, 0.6);
  }

  destroy(): void {
    this.synth?.dispose();
    this.noiseSynth?.dispose();
    this.synth = null;
    this.noiseSynth = null;
    this.initialized = false;
  }
}
